import { useState, useRef } from 'react'
import { PDFDocument } from 'pdf-lib'
import { Upload, Download, FileText } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PDFCompressor() {
  const [file, setFile] = useState(null)
  const [compressedPdf, setCompressedPdf] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [originalSize, setOriginalSize] = useState(0)
  const [compressedSize, setCompressedSize] = useState(0)
  const [compressionLevel, setCompressionLevel] = useState(50)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      setOriginalSize(selectedFile.size)
      setCompressedPdf(null)
    } else if (selectedFile) {
      alert('Please select a PDF file')
    }
  }

  const compressPDF = async () => {
    if (!file) {
      alert('Please select a PDF file')
      return
    }

    setProcessing(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer)
      
      // Calculate target size based on compression level
      // Compression level: 0-100, where 100 = maximum compression (smallest size)
      const targetSizeRatio = 1 - (compressionLevel / 100)
      
      // Optimize PDF structure
      // Use object streams for better compression (enabled by default in pdf-lib)
      // Remove unnecessary metadata
      const saveOptions = {
        useObjectStreams: true, // Enable object streams for compression
        addDefaultPage: false,
        updateMetadata: false, // Don't update metadata to save space
      }

      let compressedBytes = await pdfDoc.save(saveOptions)
      let blob = new Blob([compressedBytes], { type: 'application/pdf' })
      
      // If compression level is high and we haven't achieved target, try additional optimizations
      if (compressionLevel > 50 && blob.size > originalSize * targetSizeRatio) {
        // Try with minimal metadata
        compressedBytes = await pdfDoc.save({
          ...saveOptions,
          updateMetadata: false,
        })
        blob = new Blob([compressedBytes], { type: 'application/pdf' })
      }

      // If still larger, create a new PDF with optimized structure
      if (blob.size >= originalSize && compressionLevel > 30) {
        const optimizedDoc = await PDFDocument.create()
        const pages = pdfDoc.getPages()
        
        // Copy pages with minimal overhead
        for (let i = 0; i < pages.length; i++) {
          const [copiedPage] = await optimizedDoc.copyPages(pdfDoc, [i])
          optimizedDoc.addPage(copiedPage)
        }
        
        compressedBytes = await optimizedDoc.save({
          useObjectStreams: true,
          addDefaultPage: false,
          updateMetadata: false,
        })
        blob = new Blob([compressedBytes], { type: 'application/pdf' })
      }

      const url = URL.createObjectURL(blob)
      setCompressedPdf({ url, blob })
      setCompressedSize(blob.size)
    } catch (error) {
      alert('Error compressing PDF: ' + error.message)
      console.error('Compression error:', error)
    } finally {
      setProcessing(false)
    }
  }

  const downloadCompressed = () => {
    if (!compressedPdf) return
    const link = document.createElement('a')
    link.href = compressedPdf.url
    link.download = `compressed-${file.name}`
    link.click()
  }

  const compressionRatio = originalSize > 0 
    ? ((1 - compressedSize / originalSize) * 100).toFixed(1)
    : 0

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Select PDF File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="w-5 h-5 mr-2" />
              {file ? 'Change PDF' : 'Upload PDF'}
            </Button>
            {file && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-red-600" />
                    <span className="text-sm font-medium text-gray-900">{file.name}</span>
                  </div>
                  <span className="text-sm text-gray-500">{(originalSize / 1024).toFixed(2)} KB</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">
              Compression Level: {compressionLevel}%
            </label>
            <div className="space-y-2">
              <input
                type="range"
                min="10"
                max="90"
                step="10"
                value={compressionLevel}
                onChange={(e) => setCompressionLevel(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Low (10%)</span>
                <span>Medium (50%)</span>
                <span>High (90%)</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Higher compression reduces file size more but may slightly affect quality. Target reduction: ~{compressionLevel}%
              </p>
            </div>
          </div>

          <Button
            onClick={compressPDF}
            variant="primary"
            className="w-full"
            disabled={!file || processing}
          >
            {processing ? 'Compressing PDF...' : 'Compress PDF'}
          </Button>
        </Card.Body>
      </Card>

      {compressedPdf && (
        <Card>
          <Card.Body>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Original Size</div>
                  <div className="text-lg font-bold text-gray-900">{(originalSize / 1024).toFixed(2)} KB</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Compressed Size</div>
                  <div className="text-lg font-bold text-green-600">{(compressedSize / 1024).toFixed(2)} KB</div>
                </div>
              </div>
              <div className={`rounded-lg p-4 text-center ${
                compressionRatio > 0 ? 'bg-blue-50' : compressionRatio < 0 ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                  <div className="text-sm text-gray-600 mb-1">Size Change</div>
                  <div className={`text-2xl font-bold ${
                    compressionRatio > 0 ? 'text-blue-600' : compressionRatio < 0 ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {compressionRatio > 0 ? '-' : '+'}{Math.abs(compressionRatio)}%
                  </div>
                  {compressionRatio < 0 && (
                    <p className="text-xs text-yellow-700 mt-2">
                      File size increased. Try a lower compression level or the PDF may already be optimized.
                    </p>
                  )}
                </div>
              <Button onClick={downloadCompressed} variant="primary" size="lg" className="w-full">
                <Download className="w-5 h-5 mr-2" />
                Download Compressed PDF
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {!file && (
        <Card>
          <Card.Body className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Upload a PDF file to compress it</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

